// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Transport session. Wraps trystero (nostr strategy) into typed actions for the
 * burn-handoff protocol. trystero discovers the peer over public nostr relays and
 * encrypts the signalling handshake with the room passphrase, so the relay only
 * sees ciphertext. The secret itself rides a direct WebRTC data channel (DTLS on
 * the wire) — no server is ever in the secret's path.
 *
 * Four actions:
 *   req    — receiver asks for the secret (JSON, targeted)
 *   secret — one encrypted secret frame: [iv | ciphertext] (binary, targeted)
 *   ack    — receiver confirms it decrypted + displayed (JSON, targeted)
 *   gone   — sender tells a late requester it was already delivered (JSON, targeted)
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';

const APP_ID = 'burnwell-v1-benrichardson-dev';

export interface SessionHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onReq: (peerId: string) => void;
  onSecret: (peerId: string, frame: Uint8Array) => void;
  onAck: (peerId: string) => void;
  onGone: (peerId: string) => void;
  onError: (message: string) => void;
}

export class BurnSession {
  readonly selfId = selfId;
  private room: Room | null = null;
  private reqA: MessageAction<JsonValue> | null = null;
  private secretA: MessageAction<Uint8Array> | null = null;
  private ackA: MessageAction<JsonValue> | null = null;
  private goneA: MessageAction<JsonValue> | null = null;

  constructor(
    private roomId: string,
    private pass: string,
    private handlers: SessionHandlers,
  ) {}

  /** Join the room and wire the actions. Returns false if joining threw. */
  join(): boolean {
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;
    let room: Room;
    try {
      room = joinRoom(config, this.roomId);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return false;
    }
    this.room = room;

    this.reqA = room.makeAction<JsonValue>('req');
    this.secretA = room.makeAction<Uint8Array>('secret');
    this.ackA = room.makeAction<JsonValue>('ack');
    this.goneA = room.makeAction<JsonValue>('gone');

    this.reqA.onMessage = (_data, ctx) => this.handlers.onReq(ctx.peerId);
    this.secretA.onMessage = (data, ctx) => this.handlers.onSecret(ctx.peerId, data as Uint8Array);
    this.ackA.onMessage = (_data, ctx) => this.handlers.onAck(ctx.peerId);
    this.goneA.onMessage = (_data, ctx) => this.handlers.onGone(ctx.peerId);

    room.onPeerJoin = (peerId) => this.handlers.onPeerJoin(peerId);
    room.onPeerLeave = (peerId) => this.handlers.onPeerLeave(peerId);
    return true;
  }

  sendReq(target: string): void {
    this.reqA?.send({} as unknown as JsonValue, { target }).catch(() => undefined);
  }

  sendGone(target: string): void {
    this.goneA?.send({} as unknown as JsonValue, { target }).catch(() => undefined);
  }

  sendAck(target: string): void {
    this.ackA?.send({} as unknown as JsonValue, { target }).catch(() => undefined);
  }

  /** Send the single encrypted secret frame to one verified receiver. */
  async sendSecret(target: string, frame: Uint8Array): Promise<void> {
    if (!this.secretA) throw new Error('not joined');
    await this.secretA.send(frame, { target });
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
  }
}
